import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';
import analyticsService from '../services/analytics';

// Custom hook for Firebase Analytics
export const useAnalytics = () => {
  // Through the service's buffer, not straight to the SDK: before analytics
  // has started (a sign-in returning from a redirect, on a cold boot) this
  // used to drop `sign_up` and `login` silently ([[BUGS #527]]).
  const logCustomEvent = (eventName: string, parameters?: { [key: string]: any }) => {
    analyticsService.logEvent(eventName, parameters);
  };

  // Log screen views
  const logScreenView = (screenName: string, screenClass?: string) => {
    if (analytics) {
      logEvent(analytics, 'screen_view', {
        firebase_screen: screenName,
        firebase_screen_class: screenClass || screenName
      });
    }
  };

  // Log user actions
  const logUserAction = (action: string, category: string, label?: string, value?: number) => {
    if (analytics) {
      logEvent(analytics, action, {
        event_category: category,
        event_label: label,
        value: value
      });
    }
  };

  // Pre-defined events for IndexAlpha
  const trackEvents = {
    // Authentication events
    login: (method: string) => {
      logCustomEvent('login', { method });
    },
    
    signUp: (method: string) => {
      logCustomEvent('sign_up', { method });
    },
    
    logout: () => {
      logCustomEvent('logout');
    },

    // Onboarding events
    onboardingStart: () => {
      logCustomEvent('onboarding_start');
    },
    
    onboardingComplete: (interests: string[]) => {
      logCustomEvent('onboarding_complete', {
        interests_count: interests.length,
        interests: interests.join(',')
      });
    },
    
    onboardingSkip: (step: number) => {
      logCustomEvent('onboarding_skip', { step });
    },

    // Agent interaction events
    viewAgent: (agentName: string) => {
      logCustomEvent('view_agent', { agent_name: agentName });
    },
    
    agentAnalysisView: (agentName: string, analysisType: string) => {
      logCustomEvent('agent_analysis_view', {
        agent_name: agentName,
        analysis_type: analysisType
      });
    },
    
    agentRecommendationClick: (agentName: string, recommendationType: string) => {
      logCustomEvent('agent_recommendation_click', {
        agent_name: agentName,
        recommendation_type: recommendationType
      });
    },

    // User engagement events
    profileUpdate: (fields: string[]) => {
      logCustomEvent('profile_update', {
        updated_fields: fields.join(','),
        fields_count: fields.length
      });
    },
    
    avatarPreferenceSet: (hasPrompt: boolean) => {
      logCustomEvent('avatar_preference_set', {
        has_custom_prompt: hasPrompt
      });
    },
    
    interestUpdate: (interests: string[]) => {
      logCustomEvent('interest_update', {
        interests: interests.join(','),
        interests_count: interests.length
      });
    },

    // Feature usage events
    dashboardView: () => {
      logScreenView('dashboard');
    },
    
    settingsView: () => {
      logScreenView('settings');
    },
    
    aboutView: () => {
      logScreenView('about');
    },

    // Error events
    errorOccurred: (errorType: string, errorMessage: string, context?: string) => {
      logCustomEvent('error_occurred', {
        error_type: errorType,
        error_message: errorMessage,
        context: context
      });
    },

    // Waitlist events
    joinWaitlist: (email: string, source?: string) => {
      logCustomEvent('join_waitlist', {
        source: source || 'unknown'
      });
    }
  };

  return {
    logCustomEvent,
    logScreenView,
    logUserAction,
    trackEvents
  };
};

// Export specific tracking functions for easy access
export const trackLogin = (method: string) => {
  analyticsService.logEvent('login', { method });
};

export const trackSignUp = (method: string) => {
  analyticsService.logEvent('sign_up', { method });
};

export const trackPageView = (pageName: string) => {
  if (analytics) {
    logEvent(analytics, 'page_view', {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  }
};